import { cloneDeep, isEqual } from 'lodash';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import TextField from 'material-ui/TextField';
import IconButton from 'material-ui/IconButton';
import AutoComplete from 'material-ui/AutoComplete';
import RemoveIcon from 'material-ui/svg-icons/content/clear';
import SearchIcon from 'material-ui/svg-icons/action/search';
import api from 'services';

/** @type {Record<string, import('react').CSSProperties>} */
const styles = {
    container: {
        maxHeight: '100px',
        overflowY: 'auto',
    },
    header: {
        border: '1px solid #E0E0E0',
        borderBottom: '0',
        background: '#F5F5F5',
        padding: '10px 8px',
        fontWeight: '600',
        fontSize: '12px',
        lineHeight: '14px',
        color: '#333333',
        height: '32px',
        marginRight: '34px',
    },
    contentCol: {
        padding: '0',
    },
    content: {
        maxHeight: '282px',
        overflowY: 'auto',
        borderTop: '1px solid #DFDFDF',
        borderBottom: '1px solid #DFDFDF',
    },
    itemRow: {
        height: '28px',
        border: '1px solid #DFDFDF',
        borderBottom: '0',
        overflow: 'hidden',
    },
    itemCol: {
        padding: '8px',
        height: '28px',
        fontSize: '12px',
        lineHeight: '14px',
        marginRight: '34px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    inputCol: {
        position: 'relative',
        marginTop: '-1px',
        padding: '0 8px',
        height: '28px',
        fontSize: '12px',
        lineHeight: '14px',
        color: '#9E9E9E',
        border: '1px solid #DFDFDF',
        marginRight: '34px',
    },
    actionsCol: {
        float: 'right',
        padding: '0',
        textAlign: 'center',
        width: '34px',
        height: '28px',
        fontSize: '12px',
        lineHeight: '14px',
        borderLeft: '1px solid #DFDFDF',
    },
    deleteBtn: {
        padding: '2px',
        width: '24px',
        height: '24px',
        lineHeight: '24px',
    },
    searchIcon: {
        position: 'absolute',
        top: '4px',
        left: '8px',
        width: '16px',
        height: '16px',
        pointerEvents: 'none',
        transform: 'scaleX(-100%)',
    },
    searchField: {
        border: '1px solid #E6E6E6',
        borderRadius: '12px',
        height: '24px',
        fontSize: '12px',
    },
    searchInput: {
        paddingLeft: '30px',
        paddingRight: '12px',
    },
};

function CertificateOfFitness({ confirmed, changeField, certificateOfFitness }) {
    const [query, setQuery] = useState('');
    const [dataSource, setDataSource] = useState([]);
    const [{ filter, cargoes }, setFilter] = useState({
        filter: '',
        cargoes: certificateOfFitness,
    });
    const prevCertificateOfFitness = useRef(() => cloneDeep(certificateOfFitness));
    const handleUpdateInput = (value) => {
        setQuery(value);
        if (value.length < 2) return;
        api.getCargoNamesAutocomplete(value)
            .then((res) => {
                const cargoes = res.data.data.slice(0, 100).map(cargo => ({
                    _id: cargo._id,
                    name: cargo.name,
                }));
                setDataSource(cargoes);
            })
            .catch(console.error);
    };
    const updateFilter = (newFilter, newCargoes) => {
        const lowerFilter = newFilter.toLowerCase();
        setFilter({
            filter: newFilter,
            cargoes: (newCargoes || certificateOfFitness)
                .filter(cargo => cargo.name.toLowerCase().includes(lowerFilter)),
        });
    };
    const handleNewRequest = (newCargo) => {
        // case when cargo selecting using 'enter'
        if (typeof newCargo === 'string') return;
        if (certificateOfFitness && certificateOfFitness.find(cargo => cargo._id === newCargo._id)) {
            return;
        }
        const newCargoes = [...(certificateOfFitness || []), newCargo];
        changeField({
            field: 'certificateOfFitness',
            value: newCargoes,
        });
        setQuery('');
        updateFilter(filter, newCargoes);
    };
    const removeRow = (id) => {
        changeField({
            field: 'certificateOfFitness',
            value: cloneDeep(certificateOfFitness || []).filter(cargo => cargo._id !== id),
        });
    };
    useEffect(() => {
        if (!isEqual(prevCertificateOfFitness.current, certificateOfFitness)) {
            prevCertificateOfFitness.current = cloneDeep(certificateOfFitness);
            updateFilter(filter);
        }
    });
    return (
        <Fragment>
            <div className='row'>
                <div className='col-xs-6'>
                    <div style={{ margin: '12px 0', position: 'relative' }}>
                        <TextField
                            name='certOfFitFilter'
                            value={filter}
                            disabled={!confirmed}
                            fullWidth
                            onChange={(e, value) => {
                                updateFilter(value);
                            }}
                            underlineShow={false}
                            style={styles.searchField}
                            inputStyle={styles.searchInput}
                        />
                        <SearchIcon color='#888' style={styles.searchIcon} />
                    </div>
                </div>
            </div>
            <div className='row'>
                <div className='col-xs-12'>
                    <div style={styles.header}>
                        List of products
                    </div>
                    {cargoes.length > 0 && (
                        <div style={styles.content}>
                            {cargoes.map(cargo => (
                                <div style={styles.itemRow} key={cargo._id}>
                                    <div style={styles.actionsCol}>
                                        <IconButton
                                            disabled={!confirmed}
                                            onClick={() => removeRow(cargo._id)}
                                            style={styles.deleteBtn}
                                        >
                                            <RemoveIcon color='#B2B2B2' hoverColor='#a2a2a2' />
                                        </IconButton>
                                    </div>
                                    <div style={styles.itemCol}>
                                        {cargo.name}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={styles.inputCol}>
                        <AutoComplete
                            name='query'
                            placeholder='Enter cargo…'
                            disabled={!confirmed}
                            searchText={query}
                            dataSource={dataSource}
                            dataSourceConfig={{ text: 'name', value: '_id' }}
                            onUpdateInput={handleUpdateInput}
                            onNewRequest={handleNewRequest}
                            filter={AutoComplete.noFilter}
                            listStyle={{ overflow: 'auto', maxHeight: '300px' }}
                            popoverProps={{ canAutoPosition: true }}
                            maxSearchResults={50}
                            fullWidth
                            underlineShow={false}
                            textFieldStyle={{ height: '28px', fontSize: '12px' }}
                            inputStyle={{ color: '#999999' }}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default CertificateOfFitness;



// WEBPACK FOOTER //
// ./app/components/Vessels/Single/Rows/CertificateOfFitness.jsx